<script setup>
const props = defineProps({
    brands: {
        type: Array,
        default: () => [],
    },
    landingPages: {
        type: Array,
        default: () => [],
    },
    categoryPath: {
        type: String,
        default: '',
    },
});

const getLogoUrl = (logo) => {
    if (!logo) return null;
    return `/brand-images/${logo}`;
};

const getBrandUrl = (brand) => {
    // If we have a landing page for this brand in this category, use it
    if (props.categoryPath && props.landingPages.length > 0) {
        const lp = props.landingPages.find(lp => lp.slug === brand.slug);
        if (lp) {
            return `${props.categoryPath}/${lp.slug}`;
        }
    }
    return `/brands/${brand.slug}`;
};
</script>

<template>
    <div class="brand-strip" v-if="brands && brands.length > 0">
        <span class="strip-title">Producenter</span>
        <div class="brand-logos">
            <a
                v-for="brand in brands"
                :key="brand.id"
                :href="getBrandUrl(brand)"
                class="brand-item"
                :title="brand.name"
            >
                <img
                    v-if="brand.logo"
                    :src="getLogoUrl(brand.logo)"
                    :alt="brand.name"
                    class="brand-logo"
                    loading="lazy"
                    decoding="async"
                />
                <span v-else class="brand-text">{{ brand.name }}</span>
                <span class="brand-count">({{ brand.product_count }})</span>
            </a>
        </div>
    </div>
</template>

<style scoped>
/*
 * BAANDET BRUGTE 16px POLSTRING, 12px UNDER TITLEN OG 24px MELLEM MAERKERNE
 * for at vise ti links paa én raekke.
 *
 * Det er en genvej, ikke en sektion: man skimmer den og klikker videre.
 * 10/6/16 giver den samme funktion paa en tredjedel mindre hoejde, og
 * logoerne har stadig luft nok til ikke at loebe sammen.
 */
.brand-strip {
    background: var(--pc-bg-secondary, #1a1a2e);
    border: 1px solid var(--pc-border, #2a2a4a);
    border-radius: var(--pc-radius-lg, 12px);
    padding: 10px 12px;
    text-align: left;
    margin-bottom: 10px;
}

.strip-title {
    display: block;
    font-size: 12px;
    text-transform: uppercase;
    color: var(--pc-text-muted, #555577);
    letter-spacing: 0.5px;
    margin-bottom: 6px;
    font-weight: 600;
    text-align: left;
}

.brand-logos {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
}

.brand-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 8px 12px;
    text-decoration: none;
    color: var(--pc-text-primary, #e0e0ff);
    border-radius: 8px;
    transition: background var(--pc-transition, 150ms) ease;
}

.brand-item:hover {
    background: var(--pc-bg-hover, rgba(255, 255, 255, 0.05));
}

/*
    Logoet i sine egne farver, på en hvid brik.

    Der lå et `filter: brightness(0) invert(0.85)` som gjorde hver pixel sort
    og derefter lysegrå, altså en silhuet. Det virker KUN hvis filens baggrund
    er gennemsigtig. Har den en uigennemsigtig hvid baggrund, males hele
    rektanglet lysegråt, og logoet bliver til en tom kasse. Det var tilfældet
    for ASRock og GIGABYTE, mens ASUS med sin gennemsigtige baggrund så
    rigtig ud. Man kan ikke se på et logo udefra om det har det ene eller det
    andet, så filteret er den forkerte løsning.

    Og selv når silhuetten virkede, smed den mærkets farver væk: GIGABYTEs
    blå og røde logo blev til en grå klat.

    En hvid brik virker for alle tre slags: mørkt mærke på gennemsigtig,
    farvet mærke på hvid, og lyst mærke på mørk. Den koster lidt kontrast mod
    den mørke side, og det er prisen for at logoet ligner sig selv.
*/
.brand-logo {
    height: 24px;
    max-width: 80px;
    padding: 2px 4px;
    object-fit: contain;
    background: #fff;
    border-radius: 3px;
}

.brand-text {
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
}

.brand-count {
    font-size: 10px;
    color: var(--pc-text-muted, #555577);
}
</style>
